import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  coachApi,
  orderApi,
  profileApi,
  type CoachDetailGoodsItem,
  type CoachServiceTimeDay,
  type CoachServiceTimeSlot,
  type OrderSettlementResult,
  type SettlementCouponItem,
  type SettlementTripWayItem,
  type CustomerAddressItem,
} from "@/api";
import { auth } from "@/lib/auth";
import { resolveCustomerAssetUrl } from "@/lib/asset";
import { locationStore, requestCurrentLocation, type UserLocation } from "@/lib/location";
import { buildLoginRedirect } from "@/lib/navigation";
import { RequestError } from "@/lib/request";
import {
  invokeWechatJsapiPay,
  isWechatJsapiPayConfig,
  isWechatPayAlreadyPaid,
  waitForWechatPayResultReady,
} from "@/lib/wechat-pay";

interface AddressViewItem {
  id: number;
  label: string;
  labelIcon: string;
  name: string;
  gender: "mr" | "ms";
  phone: string;
  district: string;
  houseNum: string;
  isDefault: boolean;
  locationText: string;
  longitude?: number | null;
  latitude?: number | null;
}

type PayMethod = "wechat" | "balance";

type PayFeedback = {
  type: "balance" | "payment";
  title: string;
  message: string;
} | null;

type SubmittedOrderSnapshot = {
  orderId: number;
  sn?: string;
} | null;

interface ServiceTimeOption {
  text: string;
  timestamp: number;
}

const DEFAULT_GOODS_ID = 80;
const DEFAULT_COACH_ID = 115;
const BUSINESS_KEYWORDS = /公司|大厦|写字楼|中心|广场|园区|办公/i;
const SCHOOL_KEYWORDS = /学校|学院|大学|校区/i;

const DATE_TABS = [
  { label: "今天", key: "today" },
  { label: "明天", key: "tomorrow" },
  { label: "后天", key: "later" },
];

const TIME_SLOTS = ["10:00", "10:30", "11:00", "11:30", "14:00", "14:30", "15:00", "15:30", "19:00", "19:30", "20:00", "20:30"];

function parsePositiveNumber(value: string | null, fallback: number) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function parseOptionalId(value: string | null) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function toNullableNumber(value: number | string | null | undefined) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function compactText(parts: Array<string | null | undefined>) {
  return parts
    .map((part) => part?.trim() ?? "")
    .filter(Boolean)
    .join("");
}

function inferAddressLabel(source: string) {
  if (BUSINESS_KEYWORDS.test(source)) {
    return { label: "公司", labelIcon: "ri-building-line" };
  }
  if (SCHOOL_KEYWORDS.test(source)) {
    return { label: "学校", labelIcon: "ri-book-2-line" };
  }
  if (source.trim()) {
    return { label: "家", labelIcon: "ri-home-4-line" };
  }
  return { label: "其他", labelIcon: "ri-map-pin-2-line" };
}

function mapAddressItem(item: CustomerAddressItem): AddressViewItem {
  const addressText = (item.address ?? "").trim();
  const regionText = compactText([item.province, item.city, item.district]);
  const locationText = compactText([regionText, addressText]);
  const { label, labelIcon } = inferAddressLabel(`${locationText}${item.house_number ?? ""}`);

  return {
    id: item.id,
    label,
    labelIcon,
    name: (item.contact ?? "").trim(),
    gender: item.gender === 2 ? "ms" : "mr",
    phone: (item.mobile ?? "").trim(),
    district: addressText || regionText,
    houseNum: (item.house_number ?? "").trim(),
    isDefault: item.is_default === 1,
    longitude: toNullableNumber(item.longitude),
    latitude: toNullableNumber(item.latitude),
    locationText: locationText || addressText || regionText,
  };
}

function choosePreferredAddress(addresses: AddressViewItem[], preferredAddressId: number | null) {
  if (preferredAddressId) {
    const preferred = addresses.find((item) => item.id === preferredAddressId);
    if (preferred) {
      return preferred;
    }
  }
  return addresses.find((item) => item.isDefault) ?? addresses[0] ?? null;
}

function formatMoney(value?: number | string | null) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return `¥0`;
  }
  const cents = Math.round(amount * 100);
  return `¥${cents % 100 === 0 ? (cents / 100).toFixed(0) : (cents / 100).toFixed(2)}`;
}

function formatMoneyPrecise(value?: number | string | null) {
  const amount = Number(value);
  return `¥${Number.isFinite(amount) ? amount.toFixed(2) : "0.00"}`;
}

function couponAmountValue(coupon?: SettlementCouponItem | null) {
  const amount = Number(coupon?.money ?? 0);
  return Number.isFinite(amount) ? amount : 0;
}

function getBestCoupon(coupons: SettlementCouponItem[]) {
  return [...coupons].sort((left, right) => couponAmountValue(right) - couponAmountValue(left))[0] ?? null;
}

function getCouponTitle(coupon?: SettlementCouponItem | null) {
  return String(coupon?.tips ?? "").trim() || "优惠券";
}

function getCouponCondition(coupon?: SettlementCouponItem | null) {
  return String(coupon?.condition ?? "").trim() || "无门槛";
}

function getCouponEndTime(coupon?: SettlementCouponItem | null) {
  const text = String(coupon?.use_end_time_text ?? "").trim();
  if (!text) {
    return "长期有效";
  }
  return text.replace("T", " ").slice(0, 10);
}

function formatDistance(value?: number | string | null) {
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  const distance = Number(value);
  if (!Number.isFinite(distance) || distance <= 0) {
    return "";
  }
  if (distance < 1) {
    return `${Math.max(1, Math.round(distance * 1000))}m`;
  }
  return `${distance.toFixed(distance >= 100 ? 0 : 2)}km`;
}

function buildAddressSelectUrl(goodsId: number, coachId: number, qty: number, selectedAddressId: number | null) {
  const returnParams = new URLSearchParams({
    goodsId: String(goodsId),
    coachId: String(coachId),
    qty: String(qty),
  });

  const addressParams = new URLSearchParams({
    mode: "select",
    returnTo: `/order-confirm?${returnParams.toString()}`,
  });

  if (selectedAddressId) {
    addressParams.set("selectedId", String(selectedAddressId));
  }

  return `/address?${addressParams.toString()}`;
}

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

function isCouponUnavailableMessage(message: string) {
  return message.includes("优惠券");
}

function isBalanceInsufficientMessage(message: string) {
  const normalized = message.toLowerCase();
  return message.includes("余额不足") || normalized.includes("balance") || normalized.includes("insufficient");
}

function readStoredOrderNote(storageKey: string) {
  if (typeof window === "undefined") {
    return "";
  }
  try {
    return window.sessionStorage.getItem(storageKey) || "";
  } catch {
    return "";
  }
}

function parseServiceTimeToUnixSeconds(serviceTime: string) {
  const trimmed = serviceTime.trim();
  const matched = trimmed.match(/^(今天|明天|后天)\s+(\d{1,2}):(\d{2})$/);
  if (!matched) {
    return null;
  }

  const [, dayLabel, hourText, minuteText] = matched;
  const nextDate = new Date();
  nextDate.setSeconds(0, 0);
  nextDate.setHours(Number(hourText), Number(minuteText), 0, 0);
  if (dayLabel === "明天") {
    nextDate.setDate(nextDate.getDate() + 1);
  } else if (dayLabel === "后天") {
    nextDate.setDate(nextDate.getDate() + 2);
  }
  return Math.floor(nextDate.getTime() / 1000);
}

function payWayCode(method: PayMethod) {
  return method === "wechat" ? 1 : 3;
}

function buildServiceTimeOption(day: CoachServiceTimeDay, slot: CoachServiceTimeSlot): ServiceTimeOption {
  const label = String(day.time_tips ?? "").trim() || String(day.time_date ?? "").trim();
  return {
    text: label ? `${label} ${slot.time}` : String(slot.time ?? "").trim(),
    timestamp: Number(slot.timestamp),
  };
}

function findServiceTimeOption(days: CoachServiceTimeDay[], timestamp: number | null | undefined) {
  if (!timestamp) {
    return null;
  }
  for (const day of days) {
    const matchedSlot = day.time_lists?.find((slot) => Number(slot.timestamp) === timestamp && Number(slot.status) === 1);
    if (matchedSlot) {
      return buildServiceTimeOption(day, matchedSlot);
    }
  }
  return null;
}

function findFirstAvailableServiceTime(days: CoachServiceTimeDay[]) {
  for (const day of days) {
    const matchedSlot = day.time_lists?.find((slot) => Number(slot.status) === 1);
    if (matchedSlot) {
      return buildServiceTimeOption(day, matchedSlot);
    }
  }
  return null;
}

function getServiceMetaItems(item?: CoachDetailGoodsItem | null) {
  if (!item) {
    return [];
  }
  const items: string[] = [];
  if (Number(item.duration) > 0) {
    items.push(`${Number(item.duration)}分钟`);
  }
  const marketingTags = Array.isArray(item.marketing_tags)
    ? item.marketing_tags.map((tag) => String(tag || "").trim()).filter(Boolean)
    : [];
  return [...items, ...marketingTags].slice(0, 3);
}

function ServiceSwitchSheet({
  goodsList,
  loading,
  selectedGoodsId,
  selectedQty,
  onSelect,
  onIncrease,
  onDecrease,
  onClose,
  onConfirm,
}: {
  goodsList: CoachDetailGoodsItem[];
  loading: boolean;
  selectedGoodsId: number;
  selectedQty: number;
  onSelect: (goodsId: number) => void;
  onIncrease: () => void;
  onDecrease: () => void;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[75] flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
    >
      <div
        className="w-full rounded-t-3xl bg-white"
        style={{ maxWidth: 480, maxHeight: "80vh", animation: "slideUp 0.25s ease" }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 pb-3 pt-5" style={{ borderBottom: "1px solid #F5F5F5" }}>
          <span className="text-base font-bold" style={{ color: "#1A1A1A" }}>更换服务项目</span>
          <button type="button" onClick={onClose} className="flex h-8 w-8 items-center justify-center cursor-pointer">
            <i className="ri-close-line text-xl" style={{ color: "#999" }} />
          </button>
        </div>

        <div className="max-h-[56vh] overflow-y-auto px-4 py-3">
          {loading ? (
            <div className="py-10 text-center">
              <i className="ri-loader-4-line text-2xl animate-spin" style={{ color: "#FF6018" }} />
              <p className="mt-2 text-sm" style={{ color: "#999" }}>正在加载服务列表...</p>
            </div>
          ) : goodsList.length ? (
            <div className="space-y-3">
              {goodsList.map((item) => {
                const active = item.id === selectedGoodsId;
                const quantity = active ? selectedQty : 0;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onSelect(item.id)}
                    className="flex w-full items-center gap-3 rounded-2xl p-3 text-left cursor-pointer"
                    style={{
                      background: active ? "#FFF8F0" : "#fff",
                      border: `1.5px solid ${active ? "#FFB27A" : "#F0EAE0"}`,
                    }}
                  >
                    <div className="h-16 w-20 overflow-hidden rounded-xl flex-shrink-0">
                      <img
                        src={resolveCustomerAssetUrl(item.image) || goodsList[0]?.image || ""}
                        alt={item.name || "服务项目"}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="line-clamp-2 text-sm font-semibold leading-snug" style={{ color: "#1A1A1A" }}>
                            {item.name || "服务项目"}
                          </p>
                          <p className="mt-1 text-xs" style={{ color: "#AAAAAA" }}>
                            {getServiceMetaItems(item).join(" · ") || "正在加载服务信息"}
                          </p>
                        </div>
                        {active ? (
                          <div
                            className="flex h-6 w-6 items-center justify-center rounded-full flex-shrink-0"
                            style={{ background: "#FF6018", color: "#fff" }}
                          >
                            <i className="ri-check-line text-sm" />
                          </div>
                        ) : null}
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-3">
                        <span className="text-sm font-bold" style={{ color: "#FF5C00" }}>
                          {formatMoney(item.price)}
                        </span>
                        {active ? (
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                onDecrease();
                              }}
                              className="flex h-7 w-7 items-center justify-center rounded-full cursor-pointer"
                              style={{ border: "1.5px solid #DDDDDD", color: "#888" }}
                            >
                              <i className="ri-subtract-line text-xs" />
                            </button>
                            <span className="w-4 text-center text-sm font-bold" style={{ color: "#1A1A1A" }}>{quantity}</span>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                onIncrease();
                              }}
                              className="flex h-7 w-7 items-center justify-center rounded-full cursor-pointer"
                              style={{ background: "#FF5C00", color: "#fff" }}
                            >
                              <i className="ri-add-line text-xs" />
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="py-10 text-center text-sm" style={{ color: "#999" }}>
              当前技师暂无可切换的服务项目
            </div>
          )}
        </div>

        <div
          className="flex items-center justify-between gap-3 px-4 pt-3"
          style={{ borderTop: "1px solid #F5F5F5", paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
        >
          <div>
            <p className="text-xs" style={{ color: "#999" }}>已选数量</p>
            <p className="text-base font-bold" style={{ color: "#FF5C00" }}>{selectedQty}</p>
          </div>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading || !goodsList.length}
            className="whitespace-nowrap rounded-2xl px-8 py-3.5 text-sm font-bold text-white cursor-pointer disabled:cursor-not-allowed"
            style={{ background: loading || !goodsList.length ? "#E0E0E0" : "linear-gradient(90deg, #FF8C3A, #FF5C00)" }}
          >
            确认切换
          </button>
        </div>
      </div>
    </div>
  );
}

function TimePickerSheet({
  current,
  onConfirm,
  onClose,
}: {
  current: string;
  onConfirm: (timeText: string) => void;
  onClose: () => void;
}) {
  const [activeDate, setActiveDate] = useState(DATE_TABS[0].label);
  const [activeTime, setActiveTime] = useState(current.replace(/^今天 |^明天 |^后天 /, "") || "20:30");

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
    >
      <div
        className="w-full rounded-t-3xl bg-white pb-8"
        style={{ maxWidth: 480, animation: "slideUp 0.25s ease" }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex justify-center pb-1 pt-3">
          <div className="h-1 w-10 rounded-full" style={{ background: "#E0E0E0" }} />
        </div>
        <p className="py-3 text-center text-sm font-bold" style={{ color: "#1A1A1A" }}>选择服务时间</p>
        <div className="flex border-b" style={{ borderColor: "#F2F2F2" }}>
          {DATE_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveDate(tab.label)}
              className="flex-1 whitespace-nowrap py-3 text-xs font-semibold cursor-pointer transition-colors"
              style={{
                color: activeDate === tab.label ? "#FF6018" : "#999",
                borderBottom: activeDate === tab.label ? "2px solid #FF6018" : "2px solid transparent",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-4 gap-2.5 px-5 pb-3 pt-4">
          {TIME_SLOTS.map((time) => (
            <button
              key={time}
              onClick={() => setActiveTime(time)}
              className="whitespace-nowrap rounded-xl py-2.5 text-xs font-medium cursor-pointer transition-all"
              style={{
                border: activeTime === time ? "1.5px solid #FF6018" : "1.5px solid #EEEEEE",
                color: activeTime === time ? "#FF6018" : "#555",
                background: activeTime === time ? "#FFF5EE" : "#fff",
              }}
            >
              {time}
            </button>
          ))}
        </div>
        <p className="mb-4 text-center text-[11px]" style={{ color: "#BBBBBB" }}>当前仍为前端临时选择，提交订单前会继续补真实最近可服务时间</p>
        <div className="px-5">
          <button
            onClick={() => onConfirm(`${activeDate} ${activeTime}`)}
            className="w-full whitespace-nowrap rounded-2xl py-3.5 text-sm font-bold text-white cursor-pointer"
            style={{ background: "linear-gradient(90deg, #FF5C00, #FF9A3C)" }}
          >
            确认
          </button>
        </div>
      </div>
    </div>
  );
}

function RealTimePickerSheet({
  days,
  loading,
  error,
  currentTimestamp,
  onConfirm,
  onClose,
}: {
  days: CoachServiceTimeDay[];
  loading: boolean;
  error: string;
  currentTimestamp: number | null;
  onConfirm: (option: ServiceTimeOption) => void;
  onClose: () => void;
}) {
  const [activeDate, setActiveDate] = useState("");
  const [selectedTimestamp, setSelectedTimestamp] = useState<number | null>(currentTimestamp);

  useEffect(() => {
    const currentDay =
      days.find((day) => day.time_lists?.some((slot) => Number(slot.timestamp) === currentTimestamp)) ??
      days[0] ??
      null;
    setActiveDate(currentDay?.time_date ?? "");
  }, [currentTimestamp, days]);

  useEffect(() => {
    setSelectedTimestamp((current) => {
      const nextTimestamp = findServiceTimeOption(days, currentTimestamp)?.timestamp ?? currentTimestamp;
      return nextTimestamp ?? current ?? null;
    });
  }, [currentTimestamp, days]);

  const activeDay =
    days.find((day) => day.time_date === activeDate) ??
    days[0] ??
    null;

  const selectedOption = findServiceTimeOption(days, selectedTimestamp);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
    >
      <div
        className="w-full rounded-t-3xl bg-white pb-8"
        style={{ maxWidth: 480, animation: "slideUp 0.25s ease" }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex justify-center pb-1 pt-3">
          <div className="h-1 w-10 rounded-full" style={{ background: "#E0E0E0" }} />
        </div>
        <p className="py-3 text-center text-sm font-bold" style={{ color: "#1A1A1A" }}>{"\u9009\u62e9\u670d\u52a1\u65f6\u95f4"}</p>
        <div className="flex overflow-x-auto border-b px-2" style={{ borderColor: "#F2F2F2", scrollbarWidth: "none" }}>
          {days.map((day) => (
            <button
              key={day.time_date}
              type="button"
              onClick={() => setActiveDate(day.time_date)}
              className="flex-1 whitespace-nowrap px-3 py-3 text-xs font-semibold cursor-pointer transition-colors"
              style={{
                color: activeDate === day.time_date ? "#FF6018" : "#999",
                borderBottom: activeDate === day.time_date ? "2px solid #FF6018" : "2px solid transparent",
              }}
            >
              {String(day.time_tips ?? "").trim() || day.time_date}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="px-5 py-10 text-center">
            <i className="ri-loader-4-line text-2xl animate-spin" style={{ color: "#FF6018" }} />
            <p className="mt-2 text-sm" style={{ color: "#999" }}>{"\u6b63\u5728\u52a0\u8f7d\u53ef\u9884\u7ea6\u65f6\u95f4..."}</p>
          </div>
        ) : error ? (
          <div className="px-5 py-10 text-center">
            <p className="text-sm font-medium" style={{ color: "#D94A00" }}>{error}</p>
            <p className="mt-2 text-xs" style={{ color: "#AAAAAA" }}>{"\u8bf7\u5173\u95ed\u540e\u91cd\u65b0\u8fdb\u5165\u6216\u5237\u65b0\u9875\u9762"}</p>
          </div>
        ) : activeDay?.time_lists?.length ? (
          <div className="grid grid-cols-4 gap-2.5 px-5 pb-3 pt-4">
            {activeDay.time_lists.map((slot) => {
              const available = Number(slot.status) === 1;
              const reserved = Number(slot.status) === 3;
              const active = Number(slot.timestamp) === selectedTimestamp && available;
              return (
                <button
                  key={slot.timestamp}
                  type="button"
                  onClick={() => {
                    if (!available) {
                      return;
                    }
                    setSelectedTimestamp(Number(slot.timestamp));
                  }}
                  disabled={!available}
                  className="rounded-xl px-1 py-2.5 text-center transition-all"
                  style={{
                    border: active ? "1.5px solid #FF6018" : "1.5px solid #EEEEEE",
                    color: active ? "#FF6018" : available ? "#555" : "#BBBBBB",
                    background: active ? "#FFF5EE" : available ? "#fff" : "#F7F7F7",
                    cursor: available ? "pointer" : "not-allowed",
                  }}
                >
                  <div className="text-xs font-medium">{slot.time}</div>
                  <div className="mt-1 text-[10px]" style={{ color: active ? "#FF8A45" : reserved ? "#D94A00" : "#BBBBBB" }}>
                    {available ? "\u53ef\u9884\u7ea6" : reserved ? "\u5df2\u7ea6\u6ee1" : "\u4e0d\u53ef\u9009"}
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="px-5 py-10 text-center text-sm" style={{ color: "#999" }}>
            {"\u5f53\u5929\u6682\u65e0\u53ef\u9884\u7ea6\u65f6\u95f4"}
          </div>
        )}
        <div className="px-5">
          <button
            type="button"
            onClick={() => {
              if (!selectedOption) {
                return;
              }
              onConfirm(selectedOption);
            }}
            disabled={!selectedOption || loading}
            className="w-full whitespace-nowrap rounded-2xl py-3.5 text-sm font-bold text-white cursor-pointer"
            style={{ background: !selectedOption || loading ? "#E0E0E0" : "linear-gradient(90deg, #FF5C00, #FF9A3C)" }}
          >
            {"\u786e\u8ba4"}
          </button>
        </div>
      </div>
    </div>
  );
}

function OldStyledTimePickerSheet({
  days,
  loading,
  error,
  notice,
  currentTimestamp,
  onConfirm,
  onClose,
}: {
  days: CoachServiceTimeDay[];
  loading: boolean;
  error: string;
  notice?: string;
  currentTimestamp: number | null;
  onConfirm: (option: ServiceTimeOption) => void;
  onClose: () => void;
}) {
  const [activeDate, setActiveDate] = useState("");
  const [selectedTimestamp, setSelectedTimestamp] = useState<number | null>(currentTimestamp);

  useEffect(() => {
    const currentDay =
      days.find((day) => day.time_lists?.some((slot) => Number(slot.timestamp) === currentTimestamp)) ??
      days[0] ??
      null;
    setActiveDate(currentDay?.time_date ?? "");
  }, [currentTimestamp, days]);

  useEffect(() => {
    setSelectedTimestamp((current) => {
      const nextTimestamp = findServiceTimeOption(days, currentTimestamp)?.timestamp ?? currentTimestamp;
      return nextTimestamp ?? current ?? null;
    });
  }, [currentTimestamp, days]);

  const activeDay =
    days.find((day) => day.time_date === activeDate) ??
    days[0] ??
    null;

  const selectedOption =
    findServiceTimeOption(days, selectedTimestamp) ??
    (activeDay
      ? findFirstAvailableServiceTime([activeDay])
      : null);

  const handleDateChange = (date: string) => {
    setActiveDate(date);
    const nextDay = days.find((item) => item.time_date === date);
    const nextSelected = nextDay ? findFirstAvailableServiceTime([nextDay]) : null;
    setSelectedTimestamp(nextSelected?.timestamp ?? null);
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
    >
      <div
        className="w-full rounded-t-3xl bg-white pb-8"
        style={{ maxWidth: 480, maxHeight: "85vh", display: "flex", flexDirection: "column", animation: "slideUp 0.25s ease" }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex justify-center pb-1 pt-3 flex-shrink-0">
          <div className="h-1 w-10 rounded-full" style={{ background: "#E0E0E0" }} />
        </div>
        <p className="py-3 text-center text-sm font-bold flex-shrink-0" style={{ color: "#1A1A1A" }}>选择服务时间</p>
        {notice ? (
          <div className="mx-4 mb-3 rounded-xl px-3 py-2 text-xs font-medium" style={{ background: "#FFF4EC", color: "#D94A00" }}>
            {notice}
          </div>
        ) : null}
        <div className="flex border-b flex-shrink-0" style={{ borderColor: "#F2F2F2" }}>
          {days.map((day) => (
            <button
              key={day.time_date}
              type="button"
              onClick={() => handleDateChange(day.time_date)}
              className="flex-1 whitespace-nowrap py-3 text-xs font-semibold cursor-pointer transition-colors"
              style={{
                color: activeDate === day.time_date ? "#FF6018" : "#999",
                borderBottom: activeDate === day.time_date ? "2px solid #FF6018" : "2px solid transparent",
              }}
            >
              {String(day.time_tips ?? "").trim() || day.time_date}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="px-5 py-12 text-center flex-1 flex flex-col items-center justify-center">
            <i className="ri-loader-4-line text-2xl animate-spin" style={{ color: "#FF6018" }} />
            <p className="mt-2 text-sm" style={{ color: "#999" }}>正在加载可预约时间...</p>
          </div>
        ) : error ? (
          <div className="px-5 py-12 text-center flex-1 flex flex-col items-center justify-center">
            <p className="text-sm font-medium" style={{ color: "#D94A00" }}>{error}</p>
          </div>
        ) : activeDay?.time_lists?.length ? (
          <div className="overflow-y-auto flex-1 px-4 pt-4 pb-3">
            <div className="grid grid-cols-4 gap-2">
            {activeDay.time_lists.map((slot) => {
              const available = Number(slot.status) === 1;
              const reserved = Number(slot.status) === 3;
              const active = available && Number(slot.timestamp) === selectedTimestamp;
              return (
                <button
                  key={slot.timestamp}
                  type="button"
                  onClick={() => {
                    if (!available) {
                      return;
                    }
                    setSelectedTimestamp(Number(slot.timestamp));
                  }}
                  disabled={!available}
                  className="flex flex-col items-center rounded-xl transition-all"
                  style={{
                    padding: "8px 4px 6px",
                    border: active ? "1.5px solid #FF6018" : "1.5px solid #EEEEEE",
                    background: reserved ? "#F8F8F8" : active ? "#FFF5EE" : "#fff",
                    cursor: available ? "pointer" : "not-allowed",
                    opacity: available ? 1 : 0.7,
                  }}
                >
                  <span
                    className="text-xs font-medium mb-1 leading-none"
                    style={{ color: !available ? "#CCCCCC" : active ? "#FF6018" : "#333" }}
                  >
                    {slot.time}
                  </span>
                  <span
                    className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full leading-none whitespace-nowrap"
                    style={{
                      background: !available ? "#F0F0F0" : active ? "#FF6018" : "#EEF9EE",
                      color: !available ? "#AAAAAA" : active ? "#fff" : "#3CAB3C",
                    }}
                  >
                    {reserved ? "已被预约" : available ? "可约" : "不可选"}
                  </span>
                </button>
              );
            })}
            </div>
          </div>
        ) : (
          <div className="overflow-y-auto flex-1 px-4 pt-4 pb-3">
            <div className="flex flex-col items-center justify-center py-10">
              <i className="ri-time-line text-3xl mb-2" style={{ color: "#DDDDDD" }} />
              <p className="text-sm" style={{ color: "#BBBBBB" }}>今日暂无可预约时段</p>
            </div>
          </div>
        )}

        <p className="text-center text-[11px] flex-shrink-0 mb-3" style={{ color: "#BBBBBB" }}>以实际到达时间为准</p>
        <div className="px-5 flex-shrink-0">
          <button
            type="button"
            onClick={() => {
              if (!selectedOption) {
                return;
              }
              onConfirm(selectedOption);
            }}
            disabled={!selectedOption || loading}
            className="w-full py-3.5 rounded-2xl text-white font-bold text-sm cursor-pointer whitespace-nowrap"
            style={{
              background: !selectedOption || loading ? "#E0E0E0" : "linear-gradient(90deg, #FF5C00, #FF9A3C)",
              color: !selectedOption || loading ? "#bbb" : "#fff",
            }}
          >
            确认
          </button>
        </div>
      </div>
    </div>
  );
}

function NoteEditorSheet({
  value,
  onChange,
  onConfirm,
  onClose,
}: {
  value: string;
  onChange: (value: string) => void;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
    >
      <div
        className="w-full rounded-t-3xl bg-white pb-8"
        style={{ maxWidth: 480, animation: "slideUp 0.25s ease" }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex justify-center pb-1 pt-3">
          <div className="h-1 w-10 rounded-full" style={{ background: "#E0E0E0" }} />
        </div>
        <div className="flex items-center justify-between px-5 py-3">
          <span className="text-sm font-bold" style={{ color: "#1A1A1A" }}>订单备注</span>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center cursor-pointer">
            <i className="ri-close-line text-lg" style={{ color: "#999" }} />
          </button>
        </div>
        <div className="px-5">
          <textarea
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder="如：手法轻一点，提前10分钟联系我"
            className="mt-1 w-full resize-none rounded-2xl px-4 py-3 text-sm outline-none"
            rows={4}
            maxLength={100}
            style={{ background: "#FAFAF8", border: "1px solid #EDE8E0", color: "#333" }}
          />
          <div className="mt-2 text-right text-[11px]" style={{ color: "#BBBBBB" }}>{value.length}/100</div>
        </div>
        <div className="px-5 pt-5">
          <button
            onClick={onConfirm}
            className="w-full whitespace-nowrap rounded-2xl py-3.5 text-sm font-bold text-white cursor-pointer"
            style={{ background: "linear-gradient(90deg, #FF5C00, #FF9A3C)" }}
          >
            确认
          </button>
        </div>
      </div>
    </div>
  );
}

function CouponSelectSheet({
  usableCoupons,
  disabledCoupons,
  selectedCouponId,
  onSelect,
  onClose,
}: {
  usableCoupons: SettlementCouponItem[];
  disabledCoupons: SettlementCouponItem[];
  selectedCouponId: number;
  onSelect: (couponId: number) => void;
  onClose: () => void;
}) {
  const selectedCoupon = usableCoupons.find((item) => item.cl_id === selectedCouponId) ?? null;
  const totalCount = usableCoupons.length + disabledCoupons.length;

  return (
    <div
      className="fixed inset-0 z-[76] flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
    >
      <div
        className="w-full rounded-t-3xl bg-white"
        style={{ maxWidth: 480, maxHeight: "82vh", animation: "slideUp 0.25s ease" }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex justify-center pb-1 pt-3">
          <div className="h-1 w-10 rounded-full" style={{ background: "#E0E0E0" }} />
        </div>
        <div className="flex items-start justify-between gap-4 px-5 py-3">
          <div>
            <p className="text-base font-bold" style={{ color: "#1A1A1A" }}>选择优惠券</p>
            <p className="mt-1 text-xs" style={{ color: "#999" }}>
              {usableCoupons.length ? `已为你匹配 ${usableCoupons.length} 张可用券` : "当前订单暂无可用券"}
            </p>
          </div>
          <button type="button" onClick={onClose} className="flex h-8 w-8 flex-shrink-0 items-center justify-center cursor-pointer">
            <i className="ri-close-line text-xl" style={{ color: "#999" }} />
          </button>
        </div>

        <div className="max-h-[58vh] overflow-y-auto px-4 pb-3">
          {totalCount ? (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => {
                  onSelect(0);
                  onClose();
                }}
                className="flex w-full items-center justify-between rounded-2xl px-4 py-3 text-left cursor-pointer"
                style={{
                  background: selectedCouponId === 0 ? "#FFF8F0" : "#fff",
                  border: `1.5px solid ${selectedCouponId === 0 ? "#FFB27A" : "#F0EAE0"}`,
                }}
              >
                <div className="min-w-0">
                  <p className="text-sm font-bold" style={{ color: "#1A1A1A" }}>暂不使用优惠券</p>
                  <p className="mt-1 text-xs" style={{ color: "#999" }}>本单不抵扣优惠金额</p>
                </div>
                <div
                  className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full"
                  style={{
                    background: selectedCouponId === 0 ? "#FF6018" : "transparent",
                    border: selectedCouponId === 0 ? "none" : "1.5px solid #DDDDDD",
                  }}
                >
                  {selectedCouponId === 0 ? <i className="ri-check-line text-xs text-white" /> : null}
                </div>
              </button>

              {usableCoupons.map((coupon) => {
                const active = coupon.cl_id === selectedCouponId;
                return (
                  <button
                    key={coupon.cl_id}
                    type="button"
                    onClick={() => {
                      onSelect(coupon.cl_id);
                      onClose();
                    }}
                    className="flex w-full overflow-hidden rounded-2xl text-left cursor-pointer"
                    style={{
                      background: active ? "#FFF8F0" : "#fff",
                      border: `1.5px solid ${active ? "#FFB27A" : "#F0EAE0"}`,
                    }}
                  >
                    <div
                      className="flex w-[92px] flex-shrink-0 flex-col items-center justify-center px-3 py-4"
                      style={{ background: "linear-gradient(160deg, #FF6018 0%, #FF9A3C 100%)" }}
                    >
                      <span className="text-2xl font-black leading-none text-white">{couponAmountValue(coupon)}</span>
                      <span className="mt-0.5 text-xs font-semibold text-white/80">元</span>
                      <span className="mt-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white/85" style={{ background: "rgba(255,255,255,0.18)" }}>
                        {getCouponCondition(coupon)}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1 px-4 py-3.5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-bold" style={{ color: "#1A1A1A" }}>{getCouponTitle(coupon)}</p>
                          <p className="mt-1 text-xs" style={{ color: "#999" }}>有效期至 {getCouponEndTime(coupon)}</p>
                        </div>
                        <div
                          className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full"
                          style={{
                            background: active ? "#FF6018" : "transparent",
                            border: active ? "none" : "1.5px solid #DDDDDD",
                          }}
                        >
                          {active ? <i className="ri-check-line text-xs text-white" /> : null}
                        </div>
                      </div>
                      <p className="mt-3 text-xs font-semibold" style={{ color: "#FF6018" }}>下单立减 {formatMoney(coupon.money)}</p>
                    </div>
                  </button>
                );
              })}

              {disabledCoupons.length ? (
                <div className="pt-1">
                  <p className="px-1 pb-2 text-xs font-semibold" style={{ color: "#999" }}>不可用优惠券</p>
                  <div className="space-y-2.5">
                    {disabledCoupons.map((coupon) => (
                      <div
                        key={coupon.cl_id}
                        className="flex w-full overflow-hidden rounded-2xl text-left opacity-60"
                        style={{ background: "#FAFAF8", border: "1.5px solid #EEEAE4" }}
                      >
                        <div className="flex w-[92px] flex-shrink-0 flex-col items-center justify-center px-3 py-4" style={{ background: "#E8E0D8" }}>
                          <span className="text-2xl font-black leading-none" style={{ color: "#B8AFA6" }}>{couponAmountValue(coupon)}</span>
                          <span className="mt-0.5 text-xs font-semibold" style={{ color: "#B8AFA6" }}>元</span>
                        </div>
                        <div className="min-w-0 flex-1 px-4 py-3.5">
                          <p className="truncate text-sm font-bold" style={{ color: "#999" }}>{getCouponTitle(coupon)}</p>
                          <p className="mt-1 text-xs" style={{ color: "#AAA" }}>{coupon.tips || getCouponCondition(coupon)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="py-12 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full" style={{ background: "#FFF4EC", color: "#FF8C3A" }}>
                <i className="ri-coupon-3-line text-xl" />
              </div>
              <p className="mt-3 text-sm font-semibold" style={{ color: "#1A1A1A" }}>暂无可用优惠券</p>
              <p className="mt-1 text-xs" style={{ color: "#999" }}>有可用券时会自动显示在这里</p>
            </div>
          )}
        </div>

        <div
          className="px-4 pt-3"
          style={{ borderTop: "1px solid #F5F5F5", paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
        >
          <button
            type="button"
            onClick={onClose}
            className="w-full whitespace-nowrap rounded-2xl py-3.5 text-sm font-bold text-white cursor-pointer"
            style={{ background: "linear-gradient(90deg, #FF5C00, #FF9A3C)" }}
          >
            {selectedCoupon ? `已选 ${getCouponTitle(selectedCoupon)}` : "完成"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PayProcessingOverlay() {
  return (
    <div
      className="fixed z-[90] flex items-center justify-center"
      style={{ top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.55)" }}
    >
      <div
        className="flex flex-col items-center gap-4 rounded-3xl px-10 py-8"
        style={{ background: "#fff", minWidth: 200 }}
      >
        <div
          className="h-12 w-12 animate-spin rounded-full border-4 border-t-transparent"
          style={{ borderColor: "#FF6018 transparent #FF6018 #FF6018" }}
        />
        <p className="text-sm font-medium" style={{ color: "#555" }}>支付处理中...</p>
      </div>
    </div>
  );
}

function PaySuccessOverlay({
  total,
  orderNo,
  serviceName,
  serviceTime,
  payMethod,
  onViewOrders,
  onBackHome,
}: {
  total: number;
  orderNo: string;
  serviceName: string;
  serviceTime: string;
  payMethod: PayMethod;
  onViewOrders: () => void;
  onBackHome: () => void;
}) {
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setChecked(true), 100);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div
      className="fixed z-[90] flex flex-col items-center justify-center"
      style={{ top: 0, left: 0, width: "100vw", height: "100vh", background: "#FFF9F5", overflowY: "auto" }}
    >
      <style>{`
        @keyframes scaleIn {
          from { transform: scale(0.4); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes fadeUp {
          from { transform: translateY(16px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>

      <div
        className="mb-6 flex items-center justify-center"
        style={{
          width: 96,
          height: 96,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #FF5C00, #FF9A3C)",
          animation: "scaleIn 0.45s cubic-bezier(.34,1.56,.64,1) both",
        }}
      >
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <polyline
            points="10,26 20,36 38,16"
            stroke="white"
            strokeWidth="4.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="60"
            style={{
              strokeDashoffset: checked ? 0 : 60,
              transition: "stroke-dashoffset 0.5s ease 0.3s",
            }}
          />
        </svg>
      </div>

      <div className="mb-8 text-center" style={{ animation: "fadeUp 0.4s ease 0.2s both" }}>
        <p className="mb-1 text-xl font-bold" style={{ color: "#1A1A1A" }}>支付成功</p>
        <p className="mt-2 text-4xl font-bold" style={{ color: "#FF5C00" }}>{formatMoneyPrecise(total)}</p>
      </div>

      <div
        className="mx-4 mb-8 w-full space-y-3 rounded-3xl px-5 py-4"
        style={{
          background: "#fff",
          border: "1px solid #F0EAE0",
          maxWidth: 340,
          animation: "fadeUp 0.4s ease 0.35s both",
        }}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs" style={{ color: "#AAA" }}>订单编号</span>
          <span className="font-mono text-xs font-semibold" style={{ color: "#555" }}>{orderNo}</span>
        </div>
        <div className="flex items-center justify-between" style={{ borderTop: "1px solid #F5F2EE", paddingTop: 10 }}>
          <span className="text-xs" style={{ color: "#AAA" }}>服务项目</span>
          <span className="max-w-[180px] truncate text-right text-xs font-medium" style={{ color: "#333" }}>{serviceName}</span>
        </div>
        <div className="flex items-center justify-between" style={{ borderTop: "1px solid #F5F2EE", paddingTop: 10 }}>
          <span className="text-xs" style={{ color: "#AAA" }}>服务时间</span>
          <span className="max-w-[180px] truncate text-right text-xs font-medium" style={{ color: "#333" }}>{serviceTime}</span>
        </div>
        <div className="flex items-center justify-between" style={{ borderTop: "1px solid #F5F2EE", paddingTop: 10 }}>
          <span className="text-xs" style={{ color: "#AAA" }}>支付方式</span>
          <span className="text-xs font-medium" style={{ color: "#333" }}>
            {payMethod === "wechat" ? "微信支付" : "余额支付"}
          </span>
        </div>
      </div>

      <p className="mb-6 text-xs" style={{ color: "#CCC", animation: "fadeUp 0.4s ease 0.45s both" }}>
        技师将于预约时间前联系您
      </p>

      <div className="w-full space-y-3 px-6" style={{ maxWidth: 380, animation: "fadeUp 0.4s ease 0.5s both" }}>
        <button
          onClick={onViewOrders}
          className="w-full whitespace-nowrap rounded-2xl py-4 text-sm font-bold text-white cursor-pointer"
          style={{ background: "linear-gradient(90deg, #FF5C00, #FF9A3C)" }}
        >
          查看订单
        </button>
        <button
          onClick={onBackHome}
          className="w-full whitespace-nowrap rounded-2xl py-3.5 text-sm font-semibold cursor-pointer"
          style={{ background: "#F5F2EE", color: "#888" }}
        >
          返回首页
        </button>
      </div>
    </div>
  );
}

function PayModal({
  payMethod,
  total,
  onClose,
  onPay,
}: {
  payMethod: PayMethod;
  total: number;
  onClose: () => void;
  onPay: (method: PayMethod) => void;
}) {
  const [selected, setSelected] = useState(payMethod);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
    >
      <div
        className="w-full rounded-t-3xl bg-white px-5 pb-8 pt-2"
        style={{ maxWidth: 480, animation: "slideUp 0.25s ease" }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-2 flex justify-center pb-1 pt-3">
          <div className="h-1 w-10 rounded-full" style={{ background: "#E0E0E0" }} />
        </div>
        <div className="mb-5 text-center">
          <p className="mb-1 text-xs" style={{ color: "#999" }}>实付金额</p>
          <p className="text-3xl font-bold" style={{ color: "#FF5C00" }}>{formatMoneyPrecise(total)}</p>
        </div>
        <button
          onClick={() => setSelected("wechat")}
          className="flex w-full items-center gap-3 py-3.5 cursor-pointer"
          style={{ borderBottom: "1px solid #F5F5F5" }}
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-xl flex-shrink-0" style={{ background: "#07C160" }}>
            <i className="ri-wechat-line text-xl text-white" />
          </div>
          <span className="flex-1 text-left text-sm font-medium" style={{ color: "#1A1A1A" }}>微信支付</span>
          <div
            className="flex h-5 w-5 items-center justify-center rounded-full flex-shrink-0"
            style={{
              background: selected === "wechat" ? "#FF6018" : "transparent",
              border: selected === "wechat" ? "none" : "1.5px solid #DDDDDD",
            }}
          >
            {selected === "wechat" ? <i className="ri-check-line text-xs text-white" /> : null}
          </div>
        </button>
        <button
          onClick={() => setSelected("balance")}
          className="mb-5 flex w-full items-center gap-3 py-3.5 cursor-pointer"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-xl flex-shrink-0" style={{ background: "#FF8C3A" }}>
            <i className="ri-wallet-3-line text-xl text-white" />
          </div>
          <span className="flex-1 text-left text-sm font-medium" style={{ color: "#1A1A1A" }}>余额支付</span>
          <div
            className="flex h-5 w-5 items-center justify-center rounded-full flex-shrink-0"
            style={{
              background: selected === "balance" ? "#FF6018" : "transparent",
              border: selected === "balance" ? "none" : "1.5px solid #DDDDDD",
            }}
          >
            {selected === "balance" ? <i className="ri-check-line text-xs text-white" /> : null}
          </div>
        </button>
        <button
          onClick={() => onPay(selected)}
          className="w-full whitespace-nowrap rounded-2xl py-4 text-base font-bold text-white cursor-pointer"
          style={{ background: "linear-gradient(90deg, #FF5C00, #FF9A3C)" }}
        >
          立即支付
        </button>
      </div>
    </div>
  );
}

function PayFeedbackModal({
  feedback,
  onClose,
  onSwitchWechat,
}: {
  feedback: NonNullable<PayFeedback>;
  onClose: () => void;
  onSwitchWechat: () => void;
}) {
  const isBalance = feedback.type === "balance";

  return (
    <div
      className="fixed inset-0 z-[92] flex items-center justify-center px-6"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className="w-full overflow-hidden rounded-3xl bg-white"
        style={{ maxWidth: 360, boxShadow: "0 24px 60px rgba(0,0,0,0.18)", animation: "fadeUp 0.22s ease" }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="px-6 pb-5 pt-7 text-center">
          <div
            className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl"
            style={{ background: isBalance ? "#FFF4EC" : "#FFF0F0", color: isBalance ? "#FF6018" : "#D94A00" }}
          >
            <i className={`${isBalance ? "ri-wallet-3-line" : "ri-error-warning-line"} text-2xl`} />
          </div>
          <p className="text-lg font-bold" style={{ color: "#1A1A1A" }}>{feedback.title}</p>
          <p className="mx-auto mt-2 max-w-[260px] text-sm leading-relaxed" style={{ color: "#777" }}>
            {feedback.message}
          </p>
        </div>

        {isBalance ? (
          <div className="px-5 pb-5">
            <button
              type="button"
              onClick={onSwitchWechat}
              className="mb-3 w-full whitespace-nowrap rounded-2xl py-3.5 text-sm font-bold text-white cursor-pointer"
              style={{ background: "linear-gradient(90deg, #FF5C00, #FF9A3C)" }}
            >
              换微信支付
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full whitespace-nowrap rounded-2xl py-3.5 text-sm font-bold cursor-pointer"
              style={{ background: "#FFF4EC", color: "#FF6018" }}
            >
              我知道了
            </button>
          </div>
        ) : (
          <div className="px-5 pb-5">
            <button
              type="button"
              onClick={onClose}
              className="w-full whitespace-nowrap rounded-2xl py-3.5 text-sm font-bold text-white cursor-pointer"
              style={{ background: "linear-gradient(90deg, #FF5C00, #FF9A3C)" }}
            >
              我知道了
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function OrderConfirmPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const goodsId = parsePositiveNumber(searchParams.get("goodsId"), DEFAULT_GOODS_ID);
  const coachId = parsePositiveNumber(searchParams.get("coachId"), DEFAULT_COACH_ID);
  const incomingQty = parsePositiveNumber(searchParams.get("qty"), 1);
  const preferredAddressId = parseOptionalId(searchParams.get("selectedAddressId"));
  const orderDraftStorageKey = `athome:order-confirm:note:${coachId}:${goodsId}`;

  const [addresses, setAddresses] = useState<AddressViewItem[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(preferredAddressId);
  const [selectedTripWay, setSelectedTripWay] = useState<number | null>(null);
  const [currentLocation, setCurrentLocation] = useState<UserLocation | null>(locationStore.getFresh());
  const [settlement, setSettlement] = useState<OrderSettlementResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [pageError, setPageError] = useState("");
  const [payFeedback, setPayFeedback] = useState<PayFeedback>(null);
  const [submittedOrder, setSubmittedOrder] = useState<SubmittedOrderSnapshot>(null);
  const [qty, setQty] = useState(incomingQty);
  const [serviceTimeDays, setServiceTimeDays] = useState<CoachServiceTimeDay[]>([]);
  const [serviceTimeLoading, setServiceTimeLoading] = useState(false);
  const [serviceTimeError, setServiceTimeError] = useState("");
  const [selectedServiceTime, setSelectedServiceTime] = useState<ServiceTimeOption | null>(null);
  const [timePickerNotice, setTimePickerNotice] = useState("");
  const [serviceTimeReloadKey, setServiceTimeReloadKey] = useState(0);
  const [payMethod, setPayMethod] = useState<PayMethod>("wechat");
  const [agreed, setAgreed] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const [showCouponSheet, setShowCouponSheet] = useState(false);
  const [showServiceSwitch, setShowServiceSwitch] = useState(false);
  const [serviceSwitchLoading, setServiceSwitchLoading] = useState(false);
  const [coachGoodsList, setCoachGoodsList] = useState<CoachDetailGoodsItem[]>([]);
  const [switchGoodsId, setSwitchGoodsId] = useState(goodsId);
  const [switchQty, setSwitchQty] = useState(incomingQty);
  const [noteDraft, setNoteDraft] = useState(() => readStoredOrderNote(orderDraftStorageKey));
  const [noteText, setNoteText] = useState(() => readStoredOrderNote(orderDraftStorageKey));
  const [payProcessing, setPayProcessing] = useState(false);
  const [paySuccess, setPaySuccess] = useState(false);
  const [finalPayMethod, setFinalPayMethod] = useState<PayMethod>("wechat");
  const preserveNoteOnLeaveRef = useRef(false);
  const [orderNo, setOrderNo] = useState("");
  const [selectedCouponId, setSelectedCouponId] = useState<number | null>(null);

  const redirectToLogin = () => {
    navigate(buildLoginRedirect(`${location.pathname}${location.search}`), { replace: true });
  };

  const clearStoredNote = () => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      window.sessionStorage.removeItem(orderDraftStorageKey);
    } catch {
      // ignore storage errors
    }
  };

  const leaveWithPreservedNote = (target: number | string, options?: { replace?: boolean }) => {
    preserveNoteOnLeaveRef.current = true;
    if (typeof target === "number") {
      navigate(target);
      return;
    }
    navigate(target, options);
  };

  const leaveAndClearNote = (target: number | string, options?: { replace?: boolean }) => {
    preserveNoteOnLeaveRef.current = false;
    clearStoredNote();
    setNoteDraft("");
    setNoteText("");
    if (typeof target === "number") {
      navigate(target);
      return;
    }
    navigate(target, options);
  };

  const selectedAddress = useMemo(
    () => addresses.find((item) => item.id === selectedAddressId) ?? null,
    [addresses, selectedAddressId],
  );

  const goodsItem = settlement?.goods?.[0];
  const coachInfo = (settlement?.coach ?? {}) as Record<string, unknown>;
  const tripWayOptions = settlement?.trip_way_lists ?? [];
  const selectedTripWayValue = selectedTripWay ?? settlement?.trip_way ?? null;
  const selectedTripWayItem =
    tripWayOptions.find((item) => item.type === selectedTripWayValue) ??
    tripWayOptions[0] ??
    null;

  const serviceAmount = Number(settlement?.total_goods_price ?? 0);
  const discountAmount = Number(settlement?.discount_amount ?? 0);
  const trafficAmount = Number(settlement?.car_amount ?? 0);
  const isFreeTravelFee =
    Number(settlement?.free_travel_fee ?? 0) === 1 ||
    Number(selectedTripWayItem?.free_travel_fee ?? 0) === 1 ||
    Number(coachInfo.free_travel_fee ?? coachInfo.free_travel_fee_effective ?? coachInfo.free_travel_fee_enabled ?? 0) === 1;
  const originalTrafficAmount = Number(
    settlement?.original_car_amount ?? selectedTripWayItem?.original_amount ?? trafficAmount,
  );
  const totalAmount = Number(settlement?.order_amount ?? 0);
  const usableCoupons = settlement?.coupon_lists?.usable ?? [];
  const disabledCoupons = settlement?.coupon_lists?.disabled ?? [];
  const activeCouponId = typeof selectedCouponId === "number" ? selectedCouponId : Number(settlement?.cl_id ?? 0);
  const selectedCoupon = usableCoupons.find((item) => item.cl_id === activeCouponId) ?? null;
  const couponUnavailableText = isCouponUnavailableMessage(pageError) ? pageError : "";
  const visiblePageError = couponUnavailableText ? "" : pageError;
  const couponRowText = selectedCoupon
    ? couponUnavailableText
      ? getCouponTitle(selectedCoupon)
      : `${getCouponTitle(selectedCoupon)} -${formatMoney(discountAmount || selectedCoupon.money)}`
    : couponUnavailableText
      ? "优惠券"
      : usableCoupons.length
        ? `${usableCoupons.length}张可用`
        : "暂无可用";
  const locationDistanceText = String(settlement?.distance_desc ?? "").trim() || formatDistance(settlement?.distance);
  const hasAddressForTravelFee = Boolean(selectedAddress || selectedAddressId);
  const addressTravelUnavailable = Boolean(selectedAddress && !selectedTripWayItem);
  const travelUnavailableText = selectedAddress ? "当前地址暂不支持服务" : "请完善地址";
  const shouldShowTravelDetailRows =
    addressTravelUnavailable || (!isFreeTravelFee && Boolean(selectedTripWayItem || locationDistanceText));
  const shouldShowTrafficFeeRow = isFreeTravelFee || shouldShowTravelDetailRows || hasAddressForTravelFee;
  const serviceName = String(goodsItem?.name ?? "服务项目").trim() || "服务项目";
  const serviceTimeText = selectedServiceTime?.text?.trim() || "";
  const serviceTimeDisplay = serviceTimeLoading
    ? "服务时间加载中"
    : serviceTimeText || (serviceTimeError ? "暂时无法获取" : "暂不可约");
  const serviceTimeDisplayColor = serviceTimeText ? "#FF6018" : serviceTimeError ? "#D94A00" : "#BBBBBB";
  const paySuccessServiceTime = serviceTimeText || "待确认";

  useEffect(() => {
    setQty(incomingQty);
  }, [incomingQty]);

  useEffect(() => {
    setSwitchGoodsId(goodsId);
    setSwitchQty(qty);
  }, [goodsId, qty]);

  useEffect(() => {
    const storedNote = readStoredOrderNote(orderDraftStorageKey);
    setNoteDraft(storedNote);
    setNoteText(storedNote);
    preserveNoteOnLeaveRef.current = false;
  }, [orderDraftStorageKey]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      const trimmed = noteText.trim();
      if (trimmed) {
        window.sessionStorage.setItem(orderDraftStorageKey, trimmed);
      } else {
        window.sessionStorage.removeItem(orderDraftStorageKey);
      }
    } catch {
      // ignore storage errors
    }
  }, [noteText, orderDraftStorageKey]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handlePageLeave = () => {
      if (preserveNoteOnLeaveRef.current) {
        return;
      }
      clearStoredNote();
    };

    window.addEventListener("beforeunload", handlePageLeave);
    window.addEventListener("pagehide", handlePageLeave);
    return () => {
      window.removeEventListener("beforeunload", handlePageLeave);
      window.removeEventListener("pagehide", handlePageLeave);
    };
  }, [orderDraftStorageKey]);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      if (!auth.getToken()) {
        redirectToLogin();
        return;
      }

      setLoading(true);
      setPageError("");

      try {
        const addressResult = await profileApi.getAddressList({ goods_id: goodsId, coach_id: coachId });
        if (cancelled) {
          return;
        }

        const usableAddresses = (addressResult.usable ?? []).map(mapAddressItem);
        const nextSelectedAddress = choosePreferredAddress(usableAddresses, preferredAddressId);

        setAddresses(usableAddresses);
        setSelectedAddressId(nextSelectedAddress?.id ?? null);
        setSelectedTripWay(null);

        if (!nextSelectedAddress) {
          let freshLocation = locationStore.getFresh();
          if (!freshLocation) {
            try {
              freshLocation = await requestCurrentLocation();
            } catch {
              freshLocation = null;
            }
          }
          if (!cancelled) {
            setCurrentLocation(freshLocation);
          }
        }
      } catch (error) {
        if (!cancelled) {
          if (error instanceof RequestError && error.code === 1001) {
            redirectToLogin();
            return;
          }
          setPageError(getErrorMessage(error, "确认订单初始化失败，请稍后重试"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [coachId, goodsId, preferredAddressId]);

  useEffect(() => {
    let cancelled = false;

    const loadSettlement = async () => {
      if (loading || !auth.getToken()) {
        return;
      }

      setSettlementLoading(true);
      setPageError("");

      try {
        const result = await orderApi.getOrderSettlement({
          coach_id: coachId,
          address_id: selectedAddressId ?? undefined,
          longitude: selectedAddressId ? undefined : currentLocation?.longitude,
          latitude: selectedAddressId ? undefined : currentLocation?.latitude,
          cl_id: selectedCouponId && selectedCouponId > 0 ? selectedCouponId : undefined,
          goods: [{ id: goodsId, goods_num: qty }],
        });

        if (cancelled) {
          return;
        }

        setSettlement(result);
        if (selectedTripWay === null && typeof result.trip_way === "number" && result.trip_way > 0) {
          setSelectedTripWay(result.trip_way);
        }
        const nextUsableCoupons = result.coupon_lists?.usable ?? [];
        if (selectedCouponId === null) {
          setSelectedCouponId(getBestCoupon(nextUsableCoupons)?.cl_id ?? 0);
        } else if (selectedCouponId > 0 && !nextUsableCoupons.some((item) => item.cl_id === selectedCouponId)) {
          setSelectedCouponId(getBestCoupon(nextUsableCoupons)?.cl_id ?? 0);
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        if (error instanceof RequestError && error.code === 1001) {
          redirectToLogin();
          return;
        }
        setPageError(getErrorMessage(error, "订单结算失败，请稍后重试"));
      } finally {
        if (!cancelled) {
          setSettlementLoading(false);
        }
      }
    };

    void loadSettlement();

    return () => {
      cancelled = true;
    };
  }, [coachId, currentLocation?.latitude, currentLocation?.longitude, goodsId, loading, qty, selectedAddressId, selectedCouponId]);

  useEffect(() => {
    let cancelled = false;

    const loadServiceTimes = async () => {
      if (!auth.getToken()) {
        return;
      }

      setServiceTimeLoading(true);
      setServiceTimeError("");

      try {
        const result = await orderApi.getCoachServerTime({
          coach_id: coachId,
          goods_id: goodsId,
          goods_num: qty,
        });

        if (cancelled) {
          return;
        }

        const nextDays = Array.isArray(result) ? result : [];
        setServiceTimeDays(nextDays);
        setSelectedServiceTime((current) => {
          return findServiceTimeOption(nextDays, current?.timestamp) ?? findFirstAvailableServiceTime(nextDays);
        });
      } catch (error) {
        if (cancelled) {
          return;
        }
        if (error instanceof RequestError && error.code === 1001) {
          redirectToLogin();
          return;
        }
        setServiceTimeDays([]);
        setSelectedServiceTime(null);
        setServiceTimeError(getErrorMessage(error, "服务时间加载失败，请稍后重试"));
      } finally {
        if (!cancelled) {
          setServiceTimeLoading(false);
        }
      }
    };

    void loadServiceTimes();

    return () => {
      cancelled = true;
    };
  }, [coachId, goodsId, qty, serviceTimeReloadKey]);

  const handlePay = () => {
    if (!selectedAddress) {
      leaveWithPreservedNote(buildAddressSelectUrl(goodsId, coachId, qty, selectedAddressId), { replace: true });
      return;
    }
    if (couponUnavailableText) {
      setShowCouponSheet(true);
      return;
    }
    if (!agreed || settlementLoading || !settlement) {
      return;
    }
    setShowPayModal(true);
  };

  const openAgreement = (type: "privacy" | "payment") => {
    navigate(`/agreement?type=${type}`);
  };

  const openServiceSwitch = async () => {
    setShowServiceSwitch(true);
    setServiceSwitchLoading(true);
    setSwitchGoodsId(goodsId);
    setSwitchQty(qty);

    try {
      const result = await coachApi.getCoachDetail(coachId, {
        longitude: currentLocation?.longitude ?? undefined,
        latitude: currentLocation?.latitude ?? undefined,
      });
      setCoachGoodsList(result.goods_lists ?? []);
      if (result.goods_lists?.some((item) => item.id === goodsId)) {
        setSwitchGoodsId(goodsId);
      } else if (result.goods_lists?.[0]?.id) {
        setSwitchGoodsId(result.goods_lists[0].id);
      }
    } catch (error) {
      setPageError(getErrorMessage(error, "服务项目加载失败，请稍后重试"));
      setShowServiceSwitch(false);
    } finally {
      setServiceSwitchLoading(false);
    }
  };

  const confirmSwitchService = () => {
    const nextGoodsId = switchGoodsId > 0 ? switchGoodsId : goodsId;
    const nextQty = Math.max(1, switchQty);
    const nextParams = new URLSearchParams({
      goodsId: String(nextGoodsId),
      coachId: String(coachId),
      qty: String(nextQty),
    });
    if (selectedAddressId) {
      nextParams.set("selectedAddressId", String(selectedAddressId));
    }

    if (typeof window !== "undefined") {
      try {
        const nextStorageKey = `athome:order-confirm:note:${coachId}:${nextGoodsId}`;
        const trimmedNote = noteText.trim();
        if (trimmedNote) {
          window.sessionStorage.setItem(nextStorageKey, trimmedNote);
        } else {
          window.sessionStorage.removeItem(nextStorageKey);
        }
      } catch {
        // ignore storage errors
      }
    }

    setShowServiceSwitch(false);
    if (nextGoodsId === goodsId && nextQty === qty) {
      return;
    }
    leaveWithPreservedNote(`/order-confirm?${nextParams.toString()}`, { replace: true });
  };

  const handleConfirmPay = async (method: PayMethod) => {
    setFinalPayMethod(method);
    setPayMethod(method);
    setShowPayModal(false);
    setPayProcessing(true);
    setPageError("");
    setPayFeedback(null);

    try {
      if (!settlement || !selectedAddress) {
        throw new Error("请先确认订单信息");
      }

      const appointTime = selectedServiceTime?.timestamp ?? 0;
      if (!appointTime) {
        throw new Error("请选择可用的服务时间");
      }

      let targetOrder = submittedOrder;
      if (!targetOrder) {
        const submitResult = await orderApi.submitOrder({
          coach_id: coachId,
          address_id: selectedAddress.id,
          trip_way: selectedTripWayValue ?? undefined,
          cl_id: activeCouponId > 0 ? activeCouponId : undefined,
          user_remark: undefined,
          appoint_time: appointTime,
          goods: [{ id: goodsId, goods_num: qty }],
        });
        targetOrder = { orderId: submitResult.order_id, sn: submitResult.sn };
        setSubmittedOrder(targetOrder);
      }
      setOrderNo(String(targetOrder.sn ?? targetOrder.orderId));

      const selectedPayWay = payWayCode(method);
      const prepayResult = await orderApi.prepayOrder(targetOrder.orderId, selectedPayWay);

      if (method === "wechat") {
        const mockModeEnabled = Boolean(prepayResult.config?.mock_mode);
        if (isWechatPayAlreadyPaid(prepayResult.config)) {
          await waitForWechatPayResultReady(() => orderApi.getPayResult(targetOrder.orderId));
        } else if (!mockModeEnabled && isWechatJsapiPayConfig(prepayResult.config)) {
          await invokeWechatJsapiPay(prepayResult.config);
          await waitForWechatPayResultReady(() => orderApi.getPayResult(targetOrder.orderId));
        } else {
        if (!mockModeEnabled) {
          throw new Error("当前环境未启用模拟微信支付");
        }
        const notifyResult = await orderApi.mockPayNotify(targetOrder.orderId, "success", selectedPayWay);
        if (Number(notifyResult.pay_status) !== 1) {
          throw new Error(notifyResult.message || "模拟微信支付未完成");
        }
        }
      }

      const payResult = await orderApi.getPayResult(targetOrder.orderId);
      if (Number(payResult.pay_status) !== 1) {
        throw new Error("支付结果未确认，请稍后重试");
      }

      setOrderNo(String(payResult.sn ?? targetOrder.sn ?? targetOrder.orderId));
      setSubmittedOrder(null);
      clearStoredNote();
      setNoteDraft("");
      setNoteText("");
      setPaySuccess(true);
    } catch (error) {
      if (error instanceof RequestError && error.code === 1001) {
        redirectToLogin();
        return;
      }
      const message = getErrorMessage(error, "订单支付失败，请稍后重试");
      if (message.includes("服务时间") || message.includes("时间")) {
        setPageError(message);
        setTimePickerNotice("当前服务时间已被预约，请重新选择服务时间");
        setServiceTimeReloadKey((value) => value + 1);
        setShowTimePicker(true);
      } else if (method === "balance" && isBalanceInsufficientMessage(message)) {
        setPayFeedback({
          type: "balance",
          title: "余额不足",
          message: "当前账户余额不足以支付本单，请更换微信支付，或购卡后再继续下单。",
        });
      } else {
        setPayFeedback({
          type: "payment",
          title: "支付未完成",
          message,
        });
      }
    } finally {
      setPayProcessing(false);
    }
  };

  const coachAvatar =
    resolveCustomerAssetUrl(String(coachInfo.work_photo ?? "")) ||
    "https://readdy.ai/api/search-image?query=young%20asian%20woman%20portrait%20professional%20massage%20therapist%20uniform%20clean%20background&width=80&height=80&seq=order-confirm-coach&orientation=squarish";
  const goodsImage =
    resolveCustomerAssetUrl(goodsItem?.image) ||
    "https://readdy.ai/api/search-image?query=professional%20japanese%20foot%20massage%20therapy%20relaxing%20spa%20warm%20cozy%20clean%20wooden%20floor%20towels%20natural%20lighting&width=240&height=240&seq=order-confirm-goods&orientation=squarish";

  const payDisabled =
    !selectedAddress ||
    addressTravelUnavailable ||
    !agreed ||
    settlementLoading ||
    loading ||
    !settlement ||
    payProcessing ||
    !selectedServiceTime;

  return (
    <div
      className="customer-page-shell flex min-h-screen flex-col"
      style={{ background: "#F5F4F1" }}
    >
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>

      <div
        className="sticky top-0 z-40 flex items-center gap-3 bg-white px-4 pb-3 pt-4"
        style={{ borderBottom: "1px solid #F2F2F2" }}
      >
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? leaveWithPreservedNote(-1) : leaveAndClearNote("/"))}
          className="flex h-9 w-9 items-center justify-center rounded-full cursor-pointer"
          style={{ background: "#F5F2EE" }}
        >
          <i className="ri-arrow-left-s-line text-lg" style={{ color: "#555" }} />
        </button>
        <h1 className="flex-1 text-center text-base font-bold" style={{ color: "#1A1A1A" }}>确认订单</h1>
        <div className="w-9" />
      </div>

      <div
        className="flex-1 space-y-3 overflow-y-auto px-4 pt-4"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 120px)" }}
      >
        {visiblePageError ? (
          <div
            className="rounded-2xl px-4 py-3 text-sm"
            style={{ background: "#FFF4EC", color: "#D94A00", border: "1px solid #FFD8BF" }}
          >
            {visiblePageError}
          </div>
        ) : null}

        {!selectedAddress ? (
          <button
            onClick={() => leaveWithPreservedNote(buildAddressSelectUrl(goodsId, coachId, qty, selectedAddressId), { replace: true })}
            className="w-full rounded-2xl px-4 py-4 text-left cursor-pointer"
            style={{ background: "#FFF6EE", border: "1.5px dashed #FFAA60" }}
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl flex-shrink-0" style={{ background: "#FFE8CC" }}>
                <i className="ri-map-pin-add-line text-lg" style={{ color: "#FF6018" }} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold" style={{ color: "#FF6018" }}>尚未添加上门地址</p>
                <p className="mt-0.5 text-xs" style={{ color: "#CC8844" }}>
                  点击进入我的地址，新增或选择确认订单要使用的上门地址
                </p>
                {locationDistanceText ? (
                  <p className="mt-1 text-[11px]" style={{ color: "#B26D2F" }}>
                    当前定位距技师约 {locationDistanceText}
                  </p>
                ) : null}
              </div>
              <i className="ri-arrow-right-s-line text-lg flex-shrink-0" style={{ color: "#FFAA60" }} />
            </div>
          </button>
        ) : (
          <button
            onClick={() => leaveWithPreservedNote(buildAddressSelectUrl(goodsId, coachId, qty, selectedAddressId), { replace: true })}
            className="w-full overflow-hidden rounded-2xl text-left cursor-pointer"
            style={{ background: "#fff", border: "1.5px solid #FF9A3C" }}
          >
            {selectedAddress.isDefault ? (
              <div
                className="flex items-center gap-1.5 px-4 py-2"
                style={{ background: "#FFF8F0", borderBottom: "1px solid #FFE8CC" }}
              >
                <i className="ri-shield-check-fill text-xs" style={{ color: "#FF6018" }} />
                <span className="text-[11px] font-semibold" style={{ color: "#FF6018" }}>默认地址</span>
              </div>
            ) : null}
            <div className="flex items-start gap-3 px-4 py-3.5">
              <div className="min-w-0 flex-1">
                <div className="mb-0.5 flex items-center gap-2">
                  <span className="text-sm font-bold" style={{ color: "#1A1A1A" }}>{selectedAddress.name}</span>
                  <span className="text-xs" style={{ color: "#C0B0A0" }}>{selectedAddress.phone}</span>
                </div>
                <p className="text-xs leading-relaxed" style={{ color: "#666" }}>
                  {selectedAddress.locationText}
                  {selectedAddress.houseNum ? ` ${selectedAddress.houseNum}` : ""}
                </p>
              </div>
              <i className="ri-arrow-right-s-line mt-1 text-lg flex-shrink-0" style={{ color: "#CCCCCC" }} />
            </div>
          </button>
        )}

        <div className="rounded-2xl bg-white px-4 py-4" style={{ border: "1px solid #F0EAE0" }}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="text-xs font-semibold" style={{ color: "#888" }}>服务项目</p>
            <button
              type="button"
              onClick={() => void openServiceSwitch()}
              className="flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold cursor-pointer"
              style={{ background: "#FFF4EC", color: "#FF6018" }}
            >
              <i className="ri-refresh-line text-xs" />
              更换服务
            </button>
          </div>
          <div className="flex items-center gap-3">
            <div className="h-16 w-16 overflow-hidden rounded-xl flex-shrink-0">
              <img src={goodsImage} alt={goodsItem?.name || "服务项目"} className="h-full w-full object-cover object-top" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="mb-1 text-sm font-bold leading-snug" style={{ color: "#1A1A1A" }}>
                {goodsItem?.name || "服务项目"}
              </p>
              <p className="text-xs" style={{ color: "#AAAAAA" }}>
                {[goodsItem?.duration ? `${goodsItem.duration}分钟` : "", ...(goodsItem?.marketing_tags ?? [])].filter(Boolean).join(" · ") || "正在加载服务信息"}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              <span className="text-sm font-bold" style={{ color: "#FF5C00" }}>{formatMoney(serviceAmount || goodsItem?.price)}</span>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setQty((value) => Math.max(1, value - 1))}
                  className="flex h-7 w-7 items-center justify-center rounded-full cursor-pointer"
                  style={{ border: "1.5px solid #DDDDDD", color: "#888" }}
                >
                  <i className="ri-subtract-line text-xs" />
                </button>
                <span className="w-4 text-center text-sm font-bold" style={{ color: "#1A1A1A" }}>{qty}</span>
                <button
                  onClick={() => setQty((value) => value + 1)}
                  className="flex h-7 w-7 items-center justify-center rounded-full cursor-pointer"
                  style={{ background: "#FF5C00", color: "#fff" }}
                >
                  <i className="ri-add-line text-xs" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-white px-4" style={{ border: "1px solid #F0EAE0" }}>
          <p className="pb-2 pt-4 text-xs font-semibold" style={{ color: "#888" }}>订单信息</p>

          <div className="flex items-center justify-between py-3" style={{ borderTop: "1px solid #F8F8F8" }}>
            <span className="text-sm" style={{ color: "#888" }}>服务技师</span>
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 overflow-hidden rounded-full">
                <img src={coachAvatar} alt={String(coachInfo.name ?? "技师")} className="h-full w-full object-cover object-top" />
              </div>
              <span className="text-sm font-medium" style={{ color: "#1A1A1A" }}>{String(coachInfo.name ?? "待确认")}</span>
            </div>
          </div>

          <button
            onClick={() => {
              setTimePickerNotice("");
              setShowTimePicker(true);
            }}
            className="flex w-full items-center justify-between overflow-hidden py-3 cursor-pointer"
            style={{ borderTop: "1px solid #F8F8F8" }}
          >
            <span className="text-sm" style={{ color: "#888" }}>服务时间</span>
            <div className="flex items-center gap-1">
              <span className="text-sm font-medium" style={{ color: serviceTimeDisplayColor }}>
                {serviceTimeDisplay}
              </span>
              <i className="ri-arrow-right-s-line text-base" style={{ color: "#CCCCCC" }} />
            </div>
          </button>

          <button
            type="button"
            onClick={() => setShowCouponSheet(true)}
            className="flex w-full items-center justify-between py-3 text-left cursor-pointer"
            style={{ borderTop: "1px solid #F8F8F8" }}
          >
            <span className="text-sm" style={{ color: "#888" }}>优惠券</span>
            <div className="flex min-w-0 flex-1 items-center justify-end gap-1 pl-4">
              <span
                className="block truncate text-right text-sm font-medium"
                style={{ color: couponUnavailableText ? "#D94A00" : selectedCoupon || discountAmount > 0 ? "#FF6018" : "#BBBBBB" }}
              >
                {couponRowText}
              </span>
              {couponUnavailableText ? (
                <span
                  className="flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                  style={{ background: "#FFF4EC", color: "#D94A00", border: "1px solid #FFD8BF" }}
                >
                  不可用
                </span>
              ) : null}
              <i className="ri-arrow-right-s-line text-base flex-shrink-0" style={{ color: "#CCCCCC" }} />
            </div>
          </button>

          {shouldShowTrafficFeeRow ? (
            <>
              {shouldShowTravelDetailRows ? (
                <>
                  <div className="flex items-center justify-between py-3" style={{ borderTop: "1px solid #F8F8F8" }}>
                    <span className="text-sm" style={{ color: "#888" }}>出行方式</span>
                    <span className="text-sm font-medium" style={{ color: "#FF6018" }}>
                      {selectedTripWayItem?.type_desc || travelUnavailableText}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-3" style={{ borderTop: "1px solid #F8F8F8" }}>
                    <span className="text-sm" style={{ color: "#888" }}>直线距离</span>
                    <span className="text-sm" style={{ color: "#333" }}>
                      {formatDistance(selectedTripWayItem?.distance) || locationDistanceText || travelUnavailableText}
                    </span>
                  </div>
                </>
              ) : null}
              <div className="mb-1 flex items-center justify-between py-3" style={{ borderTop: "1px solid #F8F8F8" }}>
                <span className="text-sm" style={{ color: "#888" }}>预计交通费</span>
                {addressTravelUnavailable ? (
                  <span className="text-sm font-semibold" style={{ color: "#FF6018" }}>
                    请更换地址
                  </span>
                ) : isFreeTravelFee ? (
                  <span className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: "#16A34A" }}>
                    {originalTrafficAmount > 0 && (
                      <span className="text-xs line-through" style={{ color: "#B8B8B8" }}>{formatMoney(originalTrafficAmount)}</span>
                    )}
                    免出行费
                  </span>
                ) : (
                  <span className="text-sm font-semibold" style={{ color: "#333" }}>
                    {hasAddressForTravelFee || selectedTripWayItem || locationDistanceText ? formatMoney(trafficAmount) : "请完善地址"}
                  </span>
                )}
              </div>
            </>
          ) : null}
        </div>

        <div className="rounded-2xl bg-white px-4 py-4" style={{ border: "1px solid #F0EAE0" }}>
          <p className="mb-3 text-xs font-semibold" style={{ color: "#888" }}>费用明细</p>
          <div className="space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: "#666" }}>服务费用</span>
              <span className="text-sm" style={{ color: "#333" }}>{formatMoney(serviceAmount)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: "#666" }}>优惠减免</span>
              <span className="text-sm" style={{ color: discountAmount > 0 ? "#FF6018" : "#333" }}>
                {discountAmount > 0 ? `-${formatMoney(discountAmount)}` : formatMoney(0)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: "#666" }}>交通费用</span>
              <span className="text-sm" style={{ color: isFreeTravelFee ? "#16A34A" : "#333" }}>
                {isFreeTravelFee ? "免出行费" : trafficAmount > 0 ? `+${formatMoney(trafficAmount)}` : formatMoney(0)}
              </span>
            </div>
            <div className="flex items-center justify-between pt-2.5" style={{ borderTop: "1px solid #F5F2EE" }}>
              <span className="text-sm font-bold" style={{ color: "#1A1A1A" }}>实付金额</span>
              <span className="text-lg font-bold" style={{ color: "#FF5C00" }}>{formatMoney(totalAmount)}</span>
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-white px-4" style={{ border: "1px solid #F0EAE0" }}>
          <p className="pb-2 pt-4 text-xs font-semibold" style={{ color: "#888" }}>支付方式</p>

          <button
            onClick={() => setPayMethod("wechat")}
            className="flex w-full items-center gap-3 py-3.5 cursor-pointer"
            style={{ borderTop: "1px solid #F8F8F8" }}
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-xl flex-shrink-0" style={{ background: "#07C160" }}>
              <i className="ri-wechat-line text-base text-white" />
            </div>
            <span className="flex-1 text-left text-sm font-medium" style={{ color: "#1A1A1A" }}>微信支付</span>
            <div
              className="flex h-5 w-5 items-center justify-center rounded-full flex-shrink-0"
              style={{
                background: payMethod === "wechat" ? "#FF6018" : "transparent",
                border: payMethod === "wechat" ? "none" : "1.5px solid #DDDDDD",
              }}
            >
              {payMethod === "wechat" ? <i className="ri-check-line text-xs text-white" /> : null}
            </div>
          </button>

          <button
            onClick={() => setPayMethod("balance")}
            className="mb-1 flex w-full items-center gap-3 py-3.5 cursor-pointer"
            style={{ borderTop: "1px solid #F8F8F8" }}
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-xl flex-shrink-0" style={{ background: "#FF8C3A" }}>
              <i className="ri-wallet-3-line text-base text-white" />
            </div>
            <span className="flex-1 text-left text-sm font-medium" style={{ color: "#1A1A1A" }}>余额支付</span>
            <div
              className="flex h-5 w-5 items-center justify-center rounded-full flex-shrink-0"
              style={{
                background: payMethod === "balance" ? "#FF6018" : "transparent",
                border: payMethod === "balance" ? "none" : "1.5px solid #DDDDDD",
              }}
            >
              {payMethod === "balance" ? <i className="ri-check-line text-xs text-white" /> : null}
            </div>
          </button>
        </div>

        <div className="flex w-fit items-center gap-2 py-1">
          <button
            type="button"
            onClick={() => setAgreed((value) => !value)}
            className="flex h-5 w-5 items-center justify-center rounded-full flex-shrink-0"
            style={{
              border: agreed ? "none" : "1.5px solid #DDDDDD",
              background: agreed ? "#FF6018" : "transparent",
            }}
          >
            {agreed ? <i className="ri-check-line text-xs text-white" /> : null}
          </button>
          <span className="text-xs" style={{ color: "#AAAAAA" }}>
            我已阅读并同意
            <button type="button" onClick={() => openAgreement("payment")} style={{ color: "#FF6018" }}>
              《支付及订单规则》
            </button>
            和
            <button type="button" onClick={() => openAgreement("privacy")} style={{ color: "#FF6018" }}>
              《隐私政策》
            </button>
          </span>
        </div>
      </div>

      <div
        className="customer-fixed-shell fixed bottom-0 flex w-full items-center gap-3 bg-white px-4 pt-3"
        style={{ borderTop: "1px solid #F0EAE0", paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
      >
        <div className="flex-1">
          <p className="text-xs" style={{ color: "#999" }}>合计</p>
          <p className="text-xl font-bold leading-tight" style={{ color: "#FF5C00" }}>
            {formatMoney(totalAmount)}
          </p>
        </div>
        <button
          onClick={handlePay}
          disabled={!couponUnavailableText && payDisabled}
          className="whitespace-nowrap rounded-2xl px-8 py-3.5 text-sm font-bold cursor-pointer"
          style={{
            background: couponUnavailableText
              ? "#FFF4EC"
              : payDisabled
                ? "#E0E0E0"
                : "linear-gradient(90deg, #FF5C00, #FF9A3C)",
            color: couponUnavailableText ? "#D94A00" : payDisabled ? "#BBBBBB" : "#fff",
            border: couponUnavailableText ? "1px solid #FFD8BF" : "none",
          }}
        >
          {settlementLoading ? "结算中..." : couponUnavailableText ? "更换优惠券" : addressTravelUnavailable ? "请更换地址" : selectedAddress ? "去支付" : "去选地址"}
        </button>
      </div>

      {showTimePicker ? (
        <OldStyledTimePickerSheet
          days={serviceTimeDays}
          loading={serviceTimeLoading}
          error={serviceTimeError}
          notice={timePickerNotice}
          currentTimestamp={selectedServiceTime?.timestamp ?? null}
          onConfirm={(option) => {
            setSelectedServiceTime(option);
            setTimePickerNotice("");
            setShowTimePicker(false);
          }}
          onClose={() => {
            setTimePickerNotice("");
            setShowTimePicker(false);
          }}
        />
      ) : null}

      {showNoteEditor ? (
        <NoteEditorSheet
          value={noteDraft}
          onChange={setNoteDraft}
          onConfirm={() => {
            setNoteText(noteDraft.trim());
            setShowNoteEditor(false);
          }}
          onClose={() => setShowNoteEditor(false)}
        />
      ) : null}

      {showCouponSheet ? (
        <CouponSelectSheet
          usableCoupons={usableCoupons}
          disabledCoupons={disabledCoupons}
          selectedCouponId={activeCouponId}
          onSelect={(couponId) => {
            if (couponUnavailableText) {
              setPageError("");
            }
            setSelectedCouponId(couponId);
          }}
          onClose={() => setShowCouponSheet(false)}
        />
      ) : null}

      {showServiceSwitch ? (
        <ServiceSwitchSheet
          goodsList={coachGoodsList}
          loading={serviceSwitchLoading}
          selectedGoodsId={switchGoodsId}
          selectedQty={switchQty}
          onSelect={(nextGoodsId) => {
            setSwitchGoodsId(nextGoodsId);
            setSwitchQty((current) => (nextGoodsId === goodsId ? Math.max(1, qty) : Math.max(1, current)));
          }}
          onIncrease={() => setSwitchQty((current) => current + 1)}
          onDecrease={() => setSwitchQty((current) => Math.max(1, current - 1))}
          onClose={() => setShowServiceSwitch(false)}
          onConfirm={confirmSwitchService}
        />
      ) : null}

      {showPayModal ? (
        <PayModal
          payMethod={payMethod}
          total={totalAmount}
          onClose={() => setShowPayModal(false)}
          onPay={handleConfirmPay}
        />
      ) : null}

      {payProcessing ? <PayProcessingOverlay /> : null}

      {payFeedback ? (
        <PayFeedbackModal
          feedback={payFeedback}
          onClose={() => setPayFeedback(null)}
          onSwitchWechat={() => {
            setPayFeedback(null);
            setPayMethod("wechat");
            setShowPayModal(true);
          }}
        />
      ) : null}

      {paySuccess ? (
        <PaySuccessOverlay
          total={totalAmount}
          orderNo={orderNo}
          serviceName={serviceName}
          serviceTime={paySuccessServiceTime}
          payMethod={finalPayMethod}
          onViewOrders={() => {
            clearStoredNote();
            navigate("/orders");
          }}
          onBackHome={() => {
            clearStoredNote();
            navigate("/");
          }}
        />
      ) : null}
    </div>
  );
}
